import React from 'react';

import { LabelInput, Switch } from '@/components/ui/inputs';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { PermissionGuard, SidePanelFormLayout } from '@/components/layout';
import { useTranslation } from '@/i18n';
import { useCreateSecurityGroupingForm } from '@/hooks/securityGrouping';


// ── Props ──────────────────────────────────────────────────────────────────

interface CreateSecurityGroupingFormPanelProps {
  onSubmit?: () => void;
  readOnly?: boolean;
}

// ── Component ──────────────────────────────────────────────────────────────

export const CreateSecurityGroupingFormPanel: React.FC<CreateSecurityGroupingFormPanelProps> = ({
  onSubmit,
  readOnly,
}) => {
  const { t: translate } = useTranslation();

  const {
    codeValue,
    groupNameValue,
    sendsAttendanceStatusValue,
    handleCodeChange,
    handleGroupNameChange,
    handleSendsAttendanceStatusChange,
    handleSubmit,
    effectiveReadOnly,
    isSubmitting,
    isReady,
    errors,
    isSubmitted,
  } = useCreateSecurityGroupingForm({ onSubmit, readOnly });

  return (
    <SidePanelFormLayout onSubmit={handleSubmit} data-testid="create-security-grouping-form">
      <SidePanelFormLayout.Fields>
        <LabelInput
          label={translate('securityGrouping:form.code')}
          value={codeValue}
          onChange={handleCodeChange}
          disabled={effectiveReadOnly || isSubmitting}
          color={errors.code && isSubmitted ? 'error' : 'default'}
          supportingText={
            errors.code?.message && isSubmitted
              ? translate(String(errors.code.message))
              : undefined
          }
          data-testid="security-grouping-code-input"
        />

        <LabelInput
          label={translate('securityGrouping:form.groupName')}
          value={groupNameValue}
          onChange={handleGroupNameChange}
          required
          disabled={effectiveReadOnly || isSubmitting}
          color={errors.groupName && isSubmitted ? 'error' : 'default'}
          supportingText={
            errors.groupName?.message && isSubmitted
              ? translate(String(errors.groupName.message))
              : undefined
          }
          data-testid="security-grouping-group-name-input"
        />

        <Switch
          label={translate('securityGrouping:form.sendsAttendanceStatus')}
          checked={sendsAttendanceStatusValue}
          onChange={handleSendsAttendanceStatusChange}
          disabled={effectiveReadOnly || isSubmitting}
          data-testid="security-grouping-sends-attendance-status-switch"
        />
      </SidePanelFormLayout.Fields>

      {!effectiveReadOnly && (
        <SidePanelFormLayout.Footer>
          <PermissionGuard action="create">
            <PrimaryButton
              type="submit"
              fullWidth
              isLoading={isSubmitting}
              disabled={!isReady || isSubmitting}
            >
              {translate('securityGrouping:form.submit')}
            </PrimaryButton>
          </PermissionGuard>
        </SidePanelFormLayout.Footer>
      )}
    </SidePanelFormLayout>
  );
};

export default CreateSecurityGroupingFormPanel;
